import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth-store';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { paymentGateways } from '@/api/paymentGateways';
import type { PaymentGatewayRow, GatewayKind, CreateGatewayBody, GatewayCredentialsBody } from '@/api/paymentGateways';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { formatRef } from '@/lib/formatRef';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Trash2, Star, ShieldCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/cn';

const BRAND_STYLES: Record<GatewayKind, { label: string; className: string }> = {
  stripe:   { label: 'Stripe',   className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  usaepay:  { label: 'USAePay',  className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cardknox: { label: 'Cardknox', className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

function KindBadge({ kind }: { kind: GatewayKind }) {
  const s = BRAND_STYLES[kind];
  return (
    <span className={cn(
      'inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider leading-none',
      s.className,
    )}>
      {s.label}
    </span>
  );
}

export function PaymentGatewaysList() {
  const user = useCurrentUser();
  const tenant = useAuth((s) => s.tenant);
  const qc = useQueryClient();
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PaymentGatewayRow | null>(null);

  const canMutate = ['super_admin', 'reseller_owner', 'office_manager'].includes(user.role);
  const customerIdFilter = user.role === 'office_manager' ? (tenant?.customerId ?? undefined) : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ['paymentGateways', customerIdFilter],
    queryFn: () => paymentGateways.list({ customer_id: customerIdFilter }),
  });
  const rows = data?.rows ?? [];

  const toggleDefault = useMutation({
    mutationFn: (row: PaymentGatewayRow) =>
      paymentGateways.update(row.id, { is_account_default: !row.is_account_default }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['paymentGateways'] }),
  });
  const toggleActive = useMutation({
    mutationFn: (row: PaymentGatewayRow) =>
      paymentGateways.update(row.id, { active: !row.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['paymentGateways'] }),
  });
  const remove = useMutation({
    mutationFn: (row: PaymentGatewayRow) => paymentGateways.remove(row.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['paymentGateways'] });
      setDeleteTarget(null);
    },
  });
  // Auth-only round trip against the gateway with the saved credentials —
  // POST /payment-gateways/:id/verify. ok=false carries the provider's own
  // rejection message (Stripe permission_error, USAePay 401 body, etc.).
  const verify = useMutation({
    mutationFn: (row: PaymentGatewayRow) => paymentGateways.verify(row.id),
    onSuccess: (result, row) => {
      if (result.ok) {
        toast({
          title: 'Connected',
          description: `${row.name}: ${result.message}`,
          className: 'border-emerald-300 bg-emerald-50 text-emerald-900',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Verification failed',
          description: `${row.name}: ${result.message}`,
        });
      }
    },
    onError: (err: unknown, row) => {
      toast({
        variant: 'destructive',
        title: 'Verification failed',
        description: err instanceof Error ? err.message : `Could not verify ${row.name}.`,
      });
    },
  });

  const columns = [
    { header: 'Ref', cell: (r: PaymentGatewayRow) => <span className="font-mono text-xs text-slate-500">{formatRef(r.ref_number, 'PGW')}</span> },
    { header: 'Name', cell: (r: PaymentGatewayRow) => (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{r.name}</span>
        {r.is_account_default && (
          <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" aria-label="Account default" />
        )}
      </div>
    ) },
    { header: 'Gateway', cell: (r: PaymentGatewayRow) => <KindBadge kind={r.kind} /> },
    { header: 'Active', cell: (r: PaymentGatewayRow) => (
      <Switch
        checked={r.active}
        onCheckedChange={() => toggleActive.mutate(r)}
        disabled={!canMutate || toggleActive.isPending}
        aria-label={r.active ? 'Deactivate gateway' : 'Activate gateway'}
      />
    ) },
    { header: '', className: 'w-10 text-end', cell: (r: PaymentGatewayRow) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Row actions"
            onClick={(e) => e.stopPropagation()}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {canMutate && (
            <DropdownMenuItem
              onClick={() => verify.mutate(r)}
              disabled={verify.isPending}
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-2" />
              {verify.isPending && verify.variables?.id === r.id ? 'Verifying…' : 'Verify credentials'}
            </DropdownMenuItem>
          )}
          {canMutate && (
            <DropdownMenuItem onClick={() => toggleDefault.mutate(r)}>
              <Star className="h-3.5 w-3.5 mr-2" />
              {r.is_account_default ? 'Clear default' : 'Set as default'}
            </DropdownMenuItem>
          )}
          {canMutate && (
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
              onClick={() => setDeleteTarget(r)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Payment gateways"
        description="Credit-card processors available to your IVR payment nodes. Credentials are encrypted at rest."
        actions={canMutate && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add gateway
          </Button>
        )}
      />
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : error ? (
        <div role="alert" className="text-sm text-red-500">
          {error instanceof Error ? error.message : 'Failed to load gateways.'}
        </div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          empty="No gateways configured. Add one to enable payment nodes in IVR trees."
        />
      )}

      <CreateGatewayDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete gateway?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              <span className="font-medium">{deleteTarget.name}</span> will be soft-deleted.
              Existing transactions keep their reference; the gateway can no longer be used by payment nodes.
            </p>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={remove.isPending}>Cancel</Button>
              </DialogClose>
              <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate(deleteTarget)}>
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ── Create dialog ─────────────────────────────────────────────────────────────

function CreateGatewayDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<GatewayKind>('stripe');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [stripeKey, setStripeKey] = useState('');
  const [stripePubKey, setStripePubKey] = useState('');
  const [usaepayKey, setUsaepayKey] = useState('');
  const [usaepayPin, setUsaepayPin] = useState('');
  const [cardknoxKey, setCardknoxKey] = useState('');
  const [setDefault, setSetDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: (body: CreateGatewayBody) => paymentGateways.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['paymentGateways'] });
      reset();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Create failed.');
    },
  });

  function reset() {
    setName(''); setKind('stripe');
    setStripeKey(''); setStripePubKey('');
    setUsaepayKey(''); setUsaepayPin(''); setCardknoxKey('');
    setSetDefault(false); setCustomerId(null); setError(null);
  }

  function buildCredentials(): GatewayCredentialsBody | null {
    if (kind === 'stripe') {
      if (!stripeKey) return null;
      return {
        kind: 'stripe',
        secret_key: stripeKey,
        ...(stripePubKey ? { publishable_key: stripePubKey } : {}),
      };
    }
    if (kind === 'usaepay') return usaepayKey && usaepayPin ? { kind: 'usaepay', api_key: usaepayKey, api_pin: usaepayPin } : null;
    return cardknoxKey ? { kind: 'cardknox', api_key: cardknoxKey } : null;
  }

  function submit() {
    setError(null);
    const creds = buildCredentials();
    if (!creds) { setError('Please fill in all credential fields.'); return; }
    if (!effectiveCustomerId) { setError('Customer required.'); return; }
    if (!name.trim()) { setError('Name required.'); return; }
    create.mutate({
      name: name.trim(), kind, customer_id: effectiveCustomerId,
      credentials: creds,
      is_account_default: setDefault,
    });
  }

  const canSubmit = name.trim().length > 0
    && effectiveCustomerId !== null
    && buildCredentials() !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add payment gateway</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="gw-name">Name</Label>
            <Input id="gw-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Main Stripe account" />
          </div>

          <div className="space-y-1">
            <Label>Gateway</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as GatewayKind)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stripe">Stripe</SelectItem>
                <SelectItem value="usaepay">USAePay</SelectItem>
                <SelectItem value="cardknox">Cardknox</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {kind === 'stripe' && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="gw-stripe-key">Secret key</Label>
                <Input
                  id="gw-stripe-key" type="password"
                  value={stripeKey}
                  onChange={(e) => setStripeKey(e.target.value)}
                  placeholder="sk_live_... or sk_test_..."
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Stripe Dashboard → Developers → API keys → Secret key.
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="gw-stripe-pub">Publishable key</Label>
                <Input
                  id="gw-stripe-pub" type="text"
                  value={stripePubKey}
                  onChange={(e) => setStripePubKey(e.target.value)}
                  placeholder="pk_live_... or pk_test_..."
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  The public key shown alongside the secret. Both must
                  be from the same environment (both <code>live</code> or both
                  <code> test</code>) — mismatch is rejected on save.
                </p>
              </div>
              <div className="rounded border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800">
                Live charging needs Stripe's "raw card data processing"
                entitlement (PCI SAQ-D). Standard test keys work without
                approval — use those to test the IVR flow first.
              </div>
            </div>
          )}
          {kind === 'usaepay' && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="gw-usaepay-key">Source key (API key)</Label>
                <Input
                  id="gw-usaepay-key" type="password"
                  value={usaepayKey}
                  onChange={(e) => setUsaepayKey(e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="gw-usaepay-pin">API PIN</Label>
                <Input
                  id="gw-usaepay-pin" type="password"
                  value={usaepayPin}
                  onChange={(e) => setUsaepayPin(e.target.value)}
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Both fields are issued in the USAePay merchant console
                  (Settings → API Keys). Together they auth via SHA-256
                  hash on each request.
                </p>
              </div>
            </div>
          )}
          {kind === 'cardknox' && (
            <div className="space-y-1">
              <Label htmlFor="gw-cardknox-key">Transaction Key (xKey)</Label>
              <Input
                id="gw-cardknox-key" type="password"
                value={cardknoxKey}
                onChange={(e) => setCardknoxKey(e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-[11px] text-muted-foreground">
                Cardknox Merchant Portal → API → Transaction Keys. Used
                directly as <code>xKey</code> on each request.
              </p>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={setDefault}
              onChange={(e) => setSetDefault(e.target.checked)}
              className="accent-primary"
            />
            Set as account default
          </label>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button type="button" onClick={submit} disabled={create.isPending || !canSubmit}>
            {create.isPending ? 'Saving…' : 'Save gateway'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
